import React, { useEffect, useState } from "react";
import LoadingState from "../../Components/LoadingState";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RoutesPaths } from "../../Router/constant";
import { resolveAuthLanding } from "../../utils/authLanding";
import SignIn from "./SignIn";

/**
 * The sign-in page, and only that. Signing up lives at /signup and the
 * assistant steps at /create-assistant; this used to be all three behind one
 * route, which meant the address bar said nothing about where the user was.
 */
export default function Login() {
  const location = useLocation();
  // Held while GET /auth/me decides where a signed-in user belongs.
  const [resolving, setResolving] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // Populated by verifyToken(). Reset to null on logout by the root reducer,
  // so a stale record can't keep a signed-out user out of the form.
  const user = useSelector((state) => state.auth.user);

  // Someone already authenticated has nothing to sign in to, however they got
  // here - a refresh, the back button, or a direct hit on "/". Where they go
  // depends on whether the account has been through the steps.
  const hasAssistant = user?.hasAssistant;

  useEffect(() => {
    if (hasAssistant === true) {
      navigate(RoutesPaths.DASHBOARD, { replace: true });
      return;
    }

    if (hasAssistant === false) {
      navigate(RoutesPaths.CREATE_ASSISTANT, { replace: true });
    }
  }, [hasAssistant, navigate]);

  // Signing in doesn't automatically mean onboarding: a user who already has
  // an assistant is done, so /auth/me routes them past the steps.
  const handleSignedIn = async () => {
    setResolving(true);

    const landing = await resolveAuthLanding(dispatch);

    navigate(landing.path, { replace: true, state: landing.state });
  };

  // Set by the OTP page after verification, by the password reset flow, or by
  // the checkout page once a payment is taken but there is no session yet.
  const {
    verified,
    passwordReset,
    paid,
    email: incomingEmail,
  } = location.state || {};
  const noticeEmail = verified || passwordReset ? incomingEmail : "";

  const notice = verified
    ? "Your email is verified. Sign in to continue."
    : passwordReset
      ? "Your password has been reset. Sign in with your new password."
      : paid
        ? "Payment complete. Sign in with the email and password you just chose."
        : "";

  // Rendering the form mid-check flashes it before the redirect.
  if (resolving) {
    return (
      <div className="app-boot">
        <LoadingState
          title="Signing you in"
          detail="Working out where to take you."
        />
      </div>
    );
  }

  return (
    <main className="onboarding">
      <SignIn
        notice={notice}
        defaultEmail={noticeEmail}
        onSuccess={handleSignedIn}
        onSwitchToSignUp={() => navigate(RoutesPaths.SIGNUP)}
      />
    </main>
  );
}
